import MultipleAutocomplete from 'components/MultipleAutocomplete';
import { CapitalActionEntryType } from 'further-types/lp/capital-action';
import { CurrencyCode } from 'further-types/lp/vehicle';
import FormField from 'further-ui/components/FormField';
import { IncomeAndExpensesLabels } from 'further-ui/labels';
import {
  CalendarDay,
  numberToCurrencyString,
  pluralize,
} from 'further-ui/utils';
import { capitalize, sumBy } from 'lodash';
import { Controller, useFormContext } from 'react-hook-form';
import { UpdateCapitalActionFormValues } from '../../hooks/useCapitalActionForm';
import { Column, Row } from 'further-ui/components/Layout';
import Typography from '@mui/material/Typography';

type Props = {
  index: number;
  vehicleId: string;
  entryType: CapitalActionEntryType;
  baseCurrency: CurrencyCode;
  disabled?: boolean;
  availableActions: Array<{
    id: string;
    counterpartyLabel: string;
    uncalledAmountLcy: number;
    ledgerDate: CalendarDay;
    type?: string;
    calledAmountLcy?: number;
    isRealisedFXGainOrLoss?: boolean;
  }>;
};

const ActionSelector: React.FC<Props> = ({
  index,
  availableActions,
  entryType,
  baseCurrency,
  disabled,
}) => {
  const { control, setValue, getValues } =
    useFormContext<UpdateCapitalActionFormValues>();

  const entries = getValues('entries') ?? [];

  const selectedActionIds = entries.flatMap((entry, entryIndex) =>
    entryIndex === index ? [] : entry.actions.map((action) => action.id),
  );

  const options = availableActions
    .filter((action) => !selectedActionIds.includes(action.id))
    .map((action) => ({
      label: `${action.counterpartyLabel} - ${numberToCurrencyString(
        action.uncalledAmountLcy,
        {
          currency: baseCurrency,
        },
      )} - ${action.ledgerDate.toReadable()} ${
        action.type
          ? `(${IncomeAndExpensesLabels[action.type] ?? action.type})`
          : ''
      }${
        action.calledAmountLcy || action.isRealisedFXGainOrLoss
          ? ` Additional balance required`
          : ''
      }`,
      renderLabel: (
        <Row fullWidth centered justify="space-between">
          <Column>
            <Typography variant="h6">{action.counterpartyLabel}</Typography>
            <Typography variant="body2">
              {action.ledgerDate.toReadable()}
            </Typography>
          </Column>
          <Typography variant="h6">
            {numberToCurrencyString(action.uncalledAmountLcy, {
              currency: baseCurrency,
            })}
          </Typography>
        </Row>
      ),
      value: action.id,
    }));

  const handleActionsChange = (actionIds: Array<string>) => {
    const currentActions = getValues(`entries.${index}.actions`).filter(
      (action) => actionIds.includes(action.id),
    );
    const newActionIds = actionIds.filter(
      (id) => !currentActions.some((action) => action.id === id),
    );

    setValue(`entries.${index}.actions`, [
      ...currentActions,
      ...newActionIds.map((id) => ({ id, capitalBreakdown: [] })),
    ]);

    const uncalledAmountLcy =
      sumBy(
        availableActions.filter((action) => actionIds.includes(action.id)),
        (a) => a.uncalledAmountLcy,
      ) ?? 0;

    setValue(`entries.${index}.totalAmountLcy`, uncalledAmountLcy);
  };

  const entryTypeConfig: Partial<
    Record<CapitalActionEntryType, { singular: string; label: string }>
  > = {
    [CapitalActionEntryType.Fees]: { singular: 'fee', label: 'fees' },
  };
  const config = entryTypeConfig[entryType];
  const labelSingular = config?.singular ?? entryType.replaceAll('-', ' ');
  const label = config?.label ?? `${labelSingular}(s)`;
  return (
    <FormField label={capitalize(label)} size="md">
      <Controller
        name={`entries.${index}.actions`}
        control={control}
        render={({ fieldState: { error }, field: { value } }) => (
          <MultipleAutocomplete
            placeholder={`Select ${label}`}
            searchFieldPlaceholder={`Search ${pluralize(labelSingular)}`}
            emptyMessage={`No ${pluralize(labelSingular)} found.`}
            fullWidth
            options={options}
            value={value.map((action) => action.id)}
            onChange={handleActionsChange}
            error={error?.message}
            disabled={disabled}
          />
        )}
      />
    </FormField>
  );
};

export default ActionSelector;
